import { PageContainer } from '@/components/page/PageContainer';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { DayPicker } from '@/components/shared/date/DayPicker';
import { TableLoader } from '@/components/shared/table/TableLoader';
import { dateToApiFormat, secondsToTimeString } from '@/helpers/dateHelper';
import { useGetStaffForDate } from '@/services/staff/useGetStaffForDate';
import { FunctionComponent, Suspense, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ViewRosterEmployee from './ViewRosterEmployee';
import { ViewRosterHeader } from './ViewRosterHeader';

const ViewRoster: FunctionComponent = () => {
  const { date } = useParams<{ date: string }>();
  const navigate = useNavigate();

  if (!date) {
    throw new Error('Date parameter is required');
  }

  const dateObj = useMemo(() => new Date(date), [date]);

  const onChangeDate = (newDate: Date) => {
    navigate(`/ess/my-calendar/${dateToApiFormat(newDate)}/view-roster`, { replace: true });
  };

  const { coworkersOnShift } = useGetStaffForDate(dateObj);

  const sortedShifts = useMemo(
    () =>
      coworkersOnShift
        ?.flatMap(day => day.shifts?.map(shift => ({ ...shift, identityId: day.identityId })) ?? [])
        .sort((a, b) => a.start - b.start) ?? [],
    [coworkersOnShift]
  );

  return (
    <PageContainer customHeader={<ViewRosterHeader />}>
      <div className="bg-white border-b-2">
        <PageSizer mode={SizerMode.PAGE}>
          <DayPicker value={dateObj} onChange={onChangeDate} />
        </PageSizer>
      </div>
      <Suspense fallback={<TableLoader />}>
        <div className="overflow-y-hidden flex flex-col ">
          <PageSizer mode={SizerMode.PAGE} className="flex-1 overflow-auto">
            <ul className="divide-y space-y-3">
              {sortedShifts.map(shift => {
                const { identityId } = shift;

                const startTime = secondsToTimeString(shift.start);
                const endTime = secondsToTimeString(shift.end);

                return (
                  <li key={`${identityId}-shift-${shift.start}-${shift.end}`}>
                    <ViewRosterEmployee identityId={identityId} startTime={startTime} endTime={endTime} />
                  </li>
                );
              })}
            </ul>
          </PageSizer>
        </div>
      </Suspense>
    </PageContainer>
  );
};

export default ViewRoster;
